import React, {ReactNode, useCallback, useEffect, useState} from 'react';
import './EntityMeta.less';
import * as DateUtil from '@/lib/dateutils';
import util from '@/lib/util';
import {deleteVersion, restoreVersion} from '@/rest/api';
import {connect} from 'react-redux';
import {getVersionsCached, invalidateVersionCache} from './VersionCache';
import {computeChanges} from './ChangeEngine';
import {describeChange} from './ChangeDescriber';
import {getEntityConfig} from './EntityMetaRegistry';
import EntityMetaHelper from './EntityMetaHelper';
import i18n from '@/i18n/i18n';
import {formatUserNameFromId, UserNameFormatOptions} from "@/Contacts/ContactFormat";
import {isPortalUser} from "@/lib/picos/currentLogin";
import {isMobileView} from "@/lib/picos/browserFns";
import clipboard from "@/lib/clipboard";
import type {EntityType} from "@/@types/entity";
import type {RootState} from "@/@types/store";
import IconButton from "../IconButton/IconButton";
import Icons from "../IconButton/Icons";
import type {EntitySchema} from "@/@types/schema";
import {EntityNames} from "@/entities";
import upFirstLetter from "@/lib/picos/upFirstLetter";
import Alert from '@/lib/alert';
import store from '@/store/store';
import EntityActions from '@/store/actions/index';
import * as EntityHooks from '@/store/hooks';
import CopyText from "../CopyText";
import {getZeroUUID} from "@/lib/picos/uuids";
import {If} from 'react-conditional-components-renderer';
import ErrorBoundaryO from "@/ErrorBoundary/ErrorBoundaryO";
import {versionRef} from '@/lib/staleSave';

/** OWA-998: shown when a task version restore is refused because the task changed after its history was read. */
const RESTORE_REFUSED_MESSAGE = 'This task was changed after you opened its history, so the version was not restored. Close the history and open it again to see the latest versions.';

/** The newest version in a history: the current version the user was viewing. */
function newestOf<T extends EntityType>(versions: T[] | null): T | null {
    return (versions ?? []).reduce<T | null>((newest, version) => {
        if (!newest) return version;
        const db = Number(version.dbAddedOn ?? 0), newestDb = Number(newest.dbAddedOn ?? 0);
        return db > newestDb || (db === newestDb && Number(version.addedOn) > Number(newest.addedOn)) ? version : newest;
    }, null);
}

/**
 * Dispatch an updated entity into the Redux store after a version delete/restore.
 * The server excludes the originating client from its websocket PATCH broadcast
 * (.except(socketId)), so we must update Redux from the HTTP response instead.
 * Mirrors the processAction pattern in App.tsx.
 */
function dispatchUpdatedEntity<T extends EntityType>(entityName: string, entity: T): void {
    const actions = EntityActions.find(e => e.name === entityName);
    if (!actions) return;

    entity.persisted = true;

    const {after} = EntityHooks.getHooks(entityName, 'find');
    for (let i = 0; i < after.length; i++) {
        const hook = after[i];
        if (!hook.async) entity = EntityHooks[hook.method](entity);
    }

    store.dispatch(actions.restore(entity));
}

function EntityMeta<T extends EntityType>(props: EntityMetaProps<T>) {
    const {showButton = true} = props;

    // Resolve entity class: from entityType prop (registry) or legacy entityClass prop
    const resolvedConfig = props.entityType ? getEntityConfig(props.entityType, props.entity) : null;
    const entityClass = resolvedConfig?.entityClass || props.entityClass;

    // Legacy inline overrides (used during migration, ignored when entityType is set)
    const ignoreFields = props.entityType ? [] : (props.ignoreFields || []);
    const nameMap = props.entityType ? {} : (props.nameMap || {});
    const transforms = props.entityType ? {} : (props.transforms || {});

    const [versions, setVersions] = useState<T[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [showAuditHistory, setShowAuditHistory] = useState(props.startOpen ?? false);
    const [restoreRefused, setRestoreRefused] = useState(false);
    const {entity, hidden} = props;
    const buttonLabel = showAuditHistory
                        ? i18n.ls('entityMeta', 'hide')
                        : i18n.ls('entityMeta', 'show');

    const schemaEntity = entityClass?.getSchema()?.entity;

    const getVersionHistory = useCallback(() => {
        if (!entityClass) return;
        const entityName = schemaEntity as EntityNames;
        getVersionsCached<T>(entityName, props.entity)
            .then(versionsResult => {
                if (versionsResult.isOk()) {
                    setVersions(versionsResult.unwrap());
                    setError(null);
                }
                else {
                    setError(versionsResult.unwrapErr().message);
                }
            });
    }, [props.entity, entityClass, schemaEntity]);

    useEffect(() => {
        if (props.startOpen) getVersionHistory();
    }, [props.startOpen, getVersionHistory]);

    function onShowHistoryClick() {
        if (!showAuditHistory) getVersionHistory();
        else setVersions(null);

        setRestoreRefused(false);
        setShowAuditHistory(prev => !prev);
    }

    const copyIdToClipboard = () => {
        clipboard.copy('firm', `${entity.id}`)
    }

    function renderBasic(entityName: string) {
        const options: UserNameFormatOptions  = {format: "FML"};
        const createdBy = entity.createdBy ? formatUserNameFromId(entity.createdBy, options) : '';
        // formatUserNameFromId returns '' for an empty id; formatDateTime/hrtToDate already
        // null-check, so `!` passes the value through unchanged. (OWA-554)
        const updatedBy = formatUserNameFromId(entity.addedBy ?? '', options);
        const createdOn = DateUtil.formatDateTime((entity.createdOn || entity.addedOn)!, false);
        const updatedOn = DateUtil.formatDateTime(util.hrtToDate((entity.dbAddedOn || entity.addedOn)!), false);

        const same = createdBy === updatedBy && createdOn === updatedOn;
        const showParent = entity.entityId && (entity.entityId !== entity.id) && (entity.entityId !== getZeroUUID());

        return <React.Fragment key={entityName}>
            <div className='entity-meta__basic'>
                <div className='entity-meta__author-label nocolor' onClick={copyIdToClipboard}>{i18n.l('entityMeta', 'createdBy')}</div>
                <div className='entity-meta__author nocolor'>{createdBy}</div>
                <div className='entity-meta__date nocolor'>{i18n.c('on')} {createdOn}</div>
                {!same && <div className='entity-meta__author-label nocolor'>{i18n.l('entityMeta', 'editedBy')}</div>}
                {!same && <div className='entity-meta__author nocolor'>{updatedBy}</div>}
                {!same && <div className='entity-meta__date nocolor'>{i18n.c('on')} {updatedOn}</div>}
            </div>
            <div className='entity-meta__ids'>
                <div>{entityName} ID</div>
                <div><CopyText useCodeStyle={true} text={entity.id}/></div>
                {showParent && <div>Parent ID</div>}
                {showParent && <div><CopyText useCodeStyle={true} text={entity.entityId}/></div>}
            </div>
        </React.Fragment>;
    }

    const {onVersionChange} = props;

    const onDeleteVersion = useCallback((curr: T) => {
        if (!entityClass) return;
        deleteVersion(schemaEntity!, curr)
            .then(result => {
                if (result.isErr()) Alert.addSimpleError(result.unwrapErr().message);
                else {
                    const updated = result.unwrap();
                    dispatchUpdatedEntity(schemaEntity!, updated);
                    invalidateVersionCache(schemaEntity!, curr.id);
                    // Parents (TaskItem, InvoiceCard, DocumentEditor, …) keep their own
                    // "current" copy of the entity in local component state and don't
                    // re-read Redux on every render. Without this callback their UI shows
                    // stale data until the user manually re-syncs.
                    onVersionChange(updated);
                    getVersionHistory();
                }
            });
    }, [entityClass, schemaEntity, getVersionHistory, onVersionChange]);

    const onRestoreVersion = useCallback((curr: T) => {
        if (!entityClass) return;
        // OWA-998: a task restore names the current version the user was viewing as its base; the
        // server refuses it (409) when the task changed since, rather than writing over that change.
        const viewing = schemaEntity === 'task' ? newestOf(versions) : null;
        let request = curr;
        if (viewing) {
            request = (typeof (curr as any).clone === 'function' ? (curr as any).clone() : {...curr}) as T;
            (request as any).baseVersion = versionRef(viewing as T & {addedOn: number});
        }
        restoreVersion(schemaEntity!, request)
            .then(result => {
                if (result.isErr() && viewing && result.unwrapErr().status === 409) {
                    // The history is out of date: the next read fetches it again
                    invalidateVersionCache(schemaEntity!, entity.id);
                    setRestoreRefused(true);
                }
                else if (result.isErr()) Alert.addSimpleError(result.unwrapErr().message);
                else {
                    setRestoreRefused(false);
                    const updated = result.unwrap();
                    dispatchUpdatedEntity(schemaEntity!, updated);
                    invalidateVersionCache(schemaEntity!, curr.id);
                    onVersionChange(updated);
                    getVersionHistory();
                }
            })
    }, [entityClass, schemaEntity, getVersionHistory, onVersionChange, versions, entity]);

    /**
     * Discard the user's in-progress local edits by handing the last saved
     * version back to the parent via onVersionChange. No server call: the
     * unsaved edits never left the client, so there's nothing to remove from
     * history. The parent's local "current" state resets to the saved row,
     * EntityMeta re-renders (props.entity is the dep of getVersionHistory),
     * and the appended-local entry disappears from the blame log.
     */
    const onDiscardLocal = useCallback((lastSaved: T) => {
        onVersionChange(lastSaved);
    }, [onVersionChange]);

    /**
     * Render changes for a single version as natural language bullet list.
     */
    function renderVersionChanges(prev: T | null, curr: T): ReactNode {
        // Use new ChangeEngine if using registry (entityType prop)
        if (props.entityType) {
            const changes = computeChanges(props.entityType, prev, curr, props.entity);
            if (changes.length === 0) return null;

            return <ul className='entity-meta__changes'>
                {changes.map((change, i) => (
                    <li key={`change-${i}`} className='entity-meta__change-item'>
                        {describeChange(change)}
                    </li>
                ))}
            </ul>;
        }

        // Legacy mode: use EntityMetaHelper.compareVersions
        const options = {
            nameMap,
            ignoreFields: ignoreFields.concat(schemaEntity + 'Id'),
            transforms,
            filters: props.filters
        };

        // prev is null for a first version; compareVersions is typed `prev: T` but already
        // handles that case, so `!` bridges the type without changing runtime. (OWA-554)
        const diffs = EntityMetaHelper.compareVersions<T>(entityClass, prev!, curr, options);
        if (diffs.length === 0) return null;

        return <ul className='entity-meta__changes'>
            {diffs.map((diff: any, j: number) => (
                <li key={`diff-${j}`} className='entity-meta__change-item'>
                    <span className='entity-meta__change-field'>{diff.name}:</span>
                    {diff.prev && <span className='entity-meta__change-prev'>{diff.prev}</span>}
                    {diff.prev && diff.curr && <span className='entity-meta__change-arrow'>&rarr;</span>}
                    {diff.curr && <span className='entity-meta__change-curr'>{diff.curr}</span>}
                </li>
            ))}
        </ul>;
    }

    function renderHistory() {
        const formatOptions: UserNameFormatOptions  = isMobileView() ? {useInitials: true} : {format: "FML"};

        if (error) return <div>
            <div>{i18n.l('entityMeta', 'serverError')}</div>
            <div className='entity-meta__error'>{error}</div>
        </div>;
        else if (versions) {
            // addedOn mode: show only one specific diff
            let versionsToShow: T[];
            if (props.addedOn) {
                const idx = versions.findIndex(v => v.addedOn === props.addedOn);
                if (idx >= 0) {
                    versionsToShow = [versions[idx]];
                } else {
                    // If addedOn matches the current entity
                    versionsToShow = [props.entity];
                }
            } else {
                versionsToShow = versions.concat(props.entity);
            }

            const allVersions = versionsToShow
                .map((curr: T, index) => {
                    let prev: T | null;
                    if (props.addedOn) {
                        // For addedOn mode, find the predecessor in the full versions array
                        const fullIdx = versions.findIndex(v => v.addedOn === curr.addedOn);
                        prev = fullIdx > 0 ? versions[fullIdx - 1] : null;
                        if (fullIdx < 0 && versions.length > 0) {
                            // curr is the entity itself; prev is last version
                            prev = versions[versions.length - 1];
                        }
                    } else {
                        prev = index > 0 ? versions[index - 1] : null;
                    }

                    const changes = renderVersionChanges(prev, curr);
                    if (!changes) return null;
                    // Mark the entry that came from appending props.entity (not from
                    // the server-returned versions). When this entry survives the
                    // diff filter, the parent has unsaved local edits — the entry
                    // visualises them as a "version" diff. We use this to switch
                    // the last-entry button from "Remove Version" (server delete)
                    // to "Discard Unsaved Changes" (client-only revert).
                    const isAppended = !props.addedOn && index === versions.length;
                    return {curr, prev, changes, isAppended};
                }).filter(util.existsFilter);

            const lastVersion = allVersions.length - 1;

            const result = allVersions.map(
                (item: { curr: T, prev: T | null, changes: ReactNode, isAppended: boolean }, index: number) => {
                    const {curr, prev, changes, isAppended} = item;
                    const isLastEntry = index === lastVersion;
                    // Unsaved-local-edits case: the last shown entry is the appended
                    // props.entity (parent has in-flight edits) AND there is a saved
                    // predecessor to fall back to. Clicking the button discards the
                    // edits client-side; no server call.
                    const isUnsavedLocal = isLastEntry && isAppended && prev != null;
                    const addedOn = DateUtil.formatDateTime(util.hrtToDate(curr.addedOn!));
                    const addedBy = formatUserNameFromId(curr.addedBy ?? '', formatOptions);
                    const status = curr.status === 'C'
                                   ? i18n.l('entityMeta', 'created')
                                   : i18n.l('entityMeta', 'edited');
                    // Include the entry index in the key: when the parent has unsaved
                    // local edits, the appended props.entity shares addedOn with the
                    // last saved version, so `version-${id}-${addedOn}` collides. The
                    // index disambiguates these adjacent entries within one render.
                    return <div key={`version-${curr.id}-${curr.addedOn}-${index}`} className='entity-meta__version'>
                        <div className='entity-meta__version-header'>
                            <div className='entity-meta__author-label'>
                                <div>{status} {i18n.c('by')} <b>{addedBy}</b> {i18n.c('on')} {addedOn}</div>
                            </div>
                            <div className='entity-meta__diff-actions'>
                                {isLastEntry && isUnsavedLocal && <IconButton type={Icons.DELETE}
                                                            title='Discard your in-progress unsaved edits and revert to the last saved version'
                                                            label='Discard Unsaved Changes'
                                                            onClick={() => onDiscardLocal(prev!)}
                                />}
                                {isLastEntry && !isUnsavedLocal && <IconButton type={Icons.DELETE}
                                                            title='Delete this version, revert to previous version'
                                                            label='Remove Version'
                                                            disabled={allVersions.length === 1}
                                                            onClick={() => onDeleteVersion(curr)}
                                />}
                                {!isLastEntry && <IconButton type={Icons.RESTORE}
                                                             title='Restore this version, making it the current version'
                                                             label='Restore Version'
                                                             onClick={() => onRestoreVersion(curr)}
                                />}
                            </div>
                        </div>

                        {changes}
                    </div>;
                }
            );

            return <React.Fragment key={`all-versions-info`}>
                {restoreRefused && <div className='entity-meta__error' role='alert'>{RESTORE_REFUSED_MESSAGE}</div>}
                <div className='entity-meta__version-count'>
                    {i18n.l('entityMeta', 'foundVersions', {versions: result.filter(util.existsFilter).length})}
                </div>
                <div className='entity-meta__versions'>{result}</div>
            </React.Fragment>;
        }
        else return <div className='entity-meta__loading'>{i18n.l('entityMeta', 'loadingBlameLog')}</div>;
    }

    let entityName = entityClass ? upFirstLetter(schemaEntity ?? '') : '';
    if (entityName === 'Matter') entityName = 'Case';

    // Bail out if a portal user.
    if (isPortalUser()) return null;

    return <div className={(props.showButtonOnly || !showAuditHistory) ? 'entity-meta--button-only' : 'entity-meta'} style={{visibility: hidden ? 'hidden' : 'visible'}}>
        <ErrorBoundaryO uniqueKey={`entity-meta-${props.entity}`}>
            {/* Show button to retrieve blame log. Could take a while! */}
            {props.misc.isConnected && showButton && (
                <IconButton type={Icons.HISTORY}
                            label={`${buttonLabel} ${i18n.l('entityMeta', 'blameLog')} for ${entityName}`}
                            color='var(--sl-color-blue-900)'
                            textColor='var(--sl-color-blue-900)'
                            onClick={onShowHistoryClick}
                            style={{margin: '.5em 0'}}
                />
            )}

            {/* Show the audit blame log, if available */}
            <If test={showAuditHistory}>{renderBasic(entityName)}</If>
            <If test={showAuditHistory}>{renderHistory()}</If>
        </ErrorBoundaryO>
    </div>
}

export type EntityMetaFilter = (t: any) => boolean;
export type EntityMetaFilters = Record<string, EntityMetaFilter>;

export type EntityMetaTransform = (t: any, before?: any, after?: any) => ReactNode;
export type EntityMetaTransforms = Record<string, EntityMetaTransform>;

export type EntityMetaNameMap = Record<string, string>;

export type EntityMetaOptions = {
    nameMap: Record<string, string>,
    ignoreFields: string[],
    transforms: EntityMetaTransforms,
    filters: EntityMetaFilters
};

export type EntityMetaCompareResult = {
    name: string,
    prev: ReactNode,
    curr: ReactNode
};

export type EntityMetaProps<T extends EntityType> = Pick<RootState, 'misc'> & {
    // New: entity type name for registry lookup (e.g., 'invoice', 'task')
    entityType?: string,

    // Legacy: entity class (used when entityType is not provided)
    entityClass?: {
        new(doc: any): any;
        getSchema(): EntitySchema;
        [key: string]: any;
    },

    // The entity itself
    entity: T,

    // Should this be hidden?
    hidden?: boolean,

    // Legacy: Fields to ignore (ignored when entityType is set)
    ignoreFields?: string[],

    // Legacy: Object map of field names to labels (ignored when entityType is set)
    nameMap?: EntityMetaNameMap,

    // Legacy: Display transformations (ignored when entityType is set)
    transforms?: EntityMetaTransforms,

    // Legacy: Filters (ignored when entityType is set)
    filters?: EntityMetaFilters,

    // Show the Show/Hide button? (default: true)
    showButton?: boolean,

    // Should we show the button only on mobile view? (default: false)
    showButtonOnMobile?: boolean,

    // Should we only show the button?
    showButtonOnly?: boolean,

    // Start with the audit log open? (default: false)
    startOpen?: boolean,

    // Show only the diff for a specific version (identified by addedOn timestamp)
    addedOn?: number,

    // Called with the new latest entity after a successful Remove Version /
    // Restore Version. Required: parents hold the entity in local component
    // state and must refresh it here, otherwise the UI shows stale data after
    // a version action until a manual re-sync. If a parent genuinely reads the
    // entity fresh from Redux every render, pass a no-op explicitly.
    onVersionChange: (entity: T) => void,
}

const mapStateToProps = (state: RootState) => {
    return {
        misc: state.misc
    };
};

export default connect(mapStateToProps)(EntityMeta);
